'use client'

import { ReactNode, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { Card } from '@vinted/web-ui'

import { Advertisement, OfferAdvertisement } from '@marketplace-web/domain/ads'
import { AdShape } from '@marketplace-web/domain/ads-data'
import { useBreakpoint } from '@marketplace-web/shared/breakpoints'
import { serverSide } from '@marketplace-web/environment/environment-util'
import {
  AuthenticatedRoute,
  CompatibleRouter,
} from '@marketplace-web/vendor-abstractions/react-router-ui'
import {
  ConversationProvider,
  ConversationListProvider,
  ConversationSidebarProvider,
} from '@marketplace-web/messaging/conversation-providers-feature'
import { INBOX_ROUTE_PATHS } from '@marketplace-web/messaging/conversation-messaging-data'

import { ROOT_URL } from 'constants/routes'

import DocumentTitle from '../../components/DocumentTitle'
import ConversationList from '../../components/ConversationList'
import RedirectToFirstConversation from '../../components/RedirectToFirstConversation'
import FavoritedItemConversationLoader from '../../components/FavoritedItemConversationLoader'
import ControlConversationLogger from '../../components/ControlConversationLogger'
import Conversation from '../../components/Conversation'

type Props = {
  websocketHostUrl?: string
  location: string
  renderWebsocketChannels: ({ websocketHostUrl }: { websocketHostUrl?: string }) => ReactNode
}

const Inbox = ({ websocketHostUrl, location, renderWebsocketChannels }: Props) => {
  const breakpoints = useBreakpoint()

  const renderPortablesInbox = () => (
    <div className="inbox-page__mobile-card">
      <div className="u-fill-width">
        <div className="inbox-page__block">
          <div className="u-flexbox u-flex-direction-column u-fill-height u-justify-content-center">
            <Routes>
              <Route path={INBOX_ROUTE_PATHS.INBOX}>
                <Route
                  index
                  element={
                    <AuthenticatedRoute redirectRailsRoute={ROOT_URL}>
                      <ConversationList />
                    </AuthenticatedRoute>
                  }
                />
                <Route
                  path={INBOX_ROUTE_PATHS.CONVERSATION}
                  element={
                    <AuthenticatedRoute redirectRailsRoute={ROOT_URL}>
                      <Conversation />
                    </AuthenticatedRoute>
                  }
                />
                <Route
                  path={INBOX_ROUTE_PATHS.WANT_IT}
                  element={
                    <AuthenticatedRoute redirectRailsRoute={ROOT_URL}>
                      <FavoritedItemConversationLoader />
                    </AuthenticatedRoute>
                  }
                />
              </Route>
            </Routes>
          </div>
        </div>
      </div>
    </div>
  )

  const renderDesktopInbox = () => (
    <div className="u-flexbox u-fill-width">
      <div className="u-fill-width">
        <Card>
          <div className="u-flexbox">
            <div className="inbox-page__block inbox-page__block--sidebar">
              <div className="u-flexbox u-flex-direction-column u-fill-height u-justify-content-center">
                <ConversationList />
              </div>
            </div>
            <div className="u-fill-width">
              <div className="inbox-page__block inbox-page__block--content">
                <Routes>
                  <Route path={INBOX_ROUTE_PATHS.INBOX}>
                    <Route
                      index
                      element={
                        <AuthenticatedRoute redirectRailsRoute={ROOT_URL}>
                          <RedirectToFirstConversation />
                        </AuthenticatedRoute>
                      }
                    />
                    <Route
                      path={INBOX_ROUTE_PATHS.CONVERSATION}
                      element={
                        <AuthenticatedRoute redirectRailsRoute={ROOT_URL}>
                          <Conversation />
                        </AuthenticatedRoute>
                      }
                    />
                    <Route
                      path={INBOX_ROUTE_PATHS.WANT_IT}
                      element={
                        <AuthenticatedRoute redirectRailsRoute={ROOT_URL}>
                          <FavoritedItemConversationLoader />
                        </AuthenticatedRoute>
                      }
                    />
                  </Route>
                </Routes>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {breakpoints.wide && (
        <Suspense>
          <div className="inbox-page__ad" data-testid="slot-container" suppressHydrationWarning>
            <Advertisement shape={AdShape.Skyscraper} id="slot-skyscraper" />
          </div>
        </Suspense>
      )}
    </div>
  )

  return (
    <div className="inbox-page__container">
      <ConversationProvider>
        <ConversationListProvider>
          <ControlConversationLogger />
          <CompatibleRouter location={location} isServerSide={serverSide}>
            <DocumentTitle>
              <ConversationSidebarProvider>
                {breakpoints.portables ? renderPortablesInbox() : renderDesktopInbox()}
              </ConversationSidebarProvider>
            </DocumentTitle>
          </CompatibleRouter>

          {renderWebsocketChannels({ websocketHostUrl })}
        </ConversationListProvider>
        <OfferAdvertisement />
      </ConversationProvider>
    </div>
  )
}

export default Inbox
